// Setup › Integrations › Import (`/c/{slug}/setup/data-import`; IMPORT, HARDEN): bring a team's history
// over from Zulip or ClickUp (services/api routes/import-runs.ts). Owners only. Six steps:
// choose the source → upload the export (stored and previewed by the API) → the mapping (each
// Zulip stream or ClickUp space and list → a new or an existing space, channel or board, or
// skipped; each person → a member, an invitation, or skipped) → the dry run (counts, conflicts,
// warnings; nothing written) → the run (a background job: phase and progress, polled every
// second; cancel) → the report (created / reused / skipped, warnings, errors; download as
// JSON). Earlier imports are listed under the wizard; opening one resumes it at its step.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Progress,
  SectionHeading,
  SettingsSection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  UploadZone,
  Wizard,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownToLineIcon,
  ClipboardCheckIcon,
  FileArchiveIcon,
  ListChecksIcon,
  MessagesSquareIcon,
  PlayIcon,
  SquareKanbanIcon,
  UploadIcon,
  UsersIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { SetupPage } from "../settings/setup-page";
import { useFeature } from "../use-feature";

type Run = OperationOutput<"importRuns.get">;
type Summary = OperationOutput<"importRuns.list">["data"][number];
type Mapping = Run["mapping"];
type Container = Run["preview"]["containers"][number];
type Person = Run["preview"]["people"][number];
type DryRun = OperationOutput<"importRuns.dryRun">;
type Source = Run["source"];
type Tally = Record<string, number>;

const STEPS = [
  { label: "Source" },
  { label: "Upload" },
  { label: "Mapping" },
  { label: "Dry run" },
  { label: "Import" },
  { label: "Report" },
];

const SOURCES: Record<
  Source,
  { label: string; hint: string; accept: string; zone: string; icon: typeof MessagesSquareIcon }
> = {
  zulip: {
    label: "Zulip",
    hint: "Streams become archived channels with their topics; the last 12 months of messages. Direct messages are never imported.",
    accept: ".gz,.tgz,.tar,application/gzip,application/x-tar",
    zone: "Zulip's organization export (.tar.gz) · up to 50 MB",
    icon: MessagesSquareIcon,
  },
  clickup: {
    label: "ClickUp",
    hint: "Spaces become spaces and lists boards, with their tasks, subtasks and assignees.",
    accept: ".csv,text/csv",
    zone: "ClickUp's CSV export (Everything › Export) · up to 50 MB",
    icon: SquareKanbanIcon,
  },
};

const runsKey = (co: string) => ["company", co, "import-runs"] as const;
const runKey = (co: string, id: string) => ["company", co, "import-runs", id] as const;

const live = (r: Pick<Run, "status">) => r.status === "queued" || r.status === "running";
const ended = (r: Pick<Run, "status">) =>
  r.status === "succeeded" || r.status === "failed" || r.status === "cancelled";

/** The wizard step an import is at. */
const stepOf = (r: Pick<Run, "status">) => (r.status === "uploaded" ? 2 : live(r) ? 4 : 5);

const STATUS_BADGE: Record<Run["status"], "neutral" | "info" | "success" | "danger" | "warning"> = {
  uploaded: "neutral",
  queued: "info",
  running: "info",
  succeeded: "success",
  failed: "danger",
  cancelled: "warning",
};

export function DataImportScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const owner = company.roles.includes("owner");
  const chat = useFeature("chat");
  const qc = useQueryClient();
  const [source, setSource] = useState<Source>("clickup");
  const [step, setStep] = useState(0);
  const [runId, setRunId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Mapping | null>(null);
  const [dry, setDry] = useState<DryRun | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: runsKey(co),
    enabled: owner,
    queryFn: () => api.client.importRuns.list({ params: { co }, query: { limit: 20 } }),
  });
  const run = useQuery({
    queryKey: runKey(co, runId ?? "none"),
    enabled: owner && runId !== null,
    queryFn: () => api.client.importRuns.get({ params: { co, id: runId as string } }),
    refetchInterval: (q) => (q.state.data && live(q.state.data) ? 1000 : false),
  });
  const current = run.data ?? null;
  const refreshList = () => void qc.invalidateQueries({ queryKey: runsKey(co) });
  // A run the job finished moves the wizard on to its report, and the list shows its end.
  const finished = step === 4 && current !== null && ended(current);
  useEffect(() => {
    if (!finished) return;
    setStep(5);
    void qc.invalidateQueries({ queryKey: runsKey(co) });
  }, [finished, qc, co]);

  const open = (r: Run) => {
    setRunId(r.id);
    setSource(r.source);
    setDraft(r.mapping);
    setDry(null);
    setError(null);
    qc.setQueryData(runKey(co, r.id), r);
    setStep(stepOf(r));
  };

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("source", source);
      form.append("file", file, file.name);
      return api.client.importRuns.create({ params: { co }, body: form });
    },
    onMutate: () => setError(null),
    onSuccess: (r) => {
      open(r);
      refreshList();
    },
    onError: (e) => setError(problemText(e, "The file was not accepted.")),
  });
  const check = useMutation({
    mutationFn: async () => {
      const id = runId as string;
      if (draft && JSON.stringify(draft) !== JSON.stringify(current?.mapping)) {
        const r = await api.client.importRuns.updateMapping({ params: { co, id }, body: draft });
        qc.setQueryData(runKey(co, id), r);
      }
      return api.client.importRuns.dryRun({ params: { co, id } });
    },
    onMutate: () => setError(null),
    onSuccess: (d) => {
      setDry(d);
      setStep(3);
    },
    onError: (e) => setError(problemText(e, "The dry run failed.")),
  });
  const start = useMutation({
    mutationFn: () => api.client.importRuns.start({ params: { co, id: runId as string } }),
    onMutate: () => setError(null),
    onSuccess: (r) => {
      qc.setQueryData(runKey(co, r.id), r);
      setStep(4);
      refreshList();
    },
    onError: (e) => setError(problemText(e, "The import did not start.")),
  });
  const cancel = useMutation({
    mutationFn: () => api.client.importRuns.cancel({ params: { co, id: runId as string } }),
    onSuccess: (r) => {
      qc.setQueryData(runKey(co, r.id), r);
      if (ended(r)) setStep(5);
      refreshList();
    },
    onError: (e) => setError(problemText(e, "The import was not cancelled.")),
  });

  const reset = () => {
    setRunId(null);
    setDraft(null);
    setDry(null);
    setError(null);
    setStep(0);
  };

  if (!owner)
    return (
      <SetupPage title="Import" slot="setup-import">
        <Notice tone="info" title="Owners only" data-slot="import-owners-only">
          Importing another tool's history and people is the company owner's call. Ask an owner of{" "}
          {company.name} to run it.
        </Notice>
      </SetupPage>
    );

  const footer = (() => {
    if (step === 0)
      return (
        <Button size="lg" onClick={() => setStep(1)}>
          Continue
        </Button>
      );
    if (step === 1)
      return (
        <Button size="lg" variant="secondary" onClick={() => setStep(0)}>
          Back
        </Button>
      );
    if (step === 2)
      return (
        <>
          <Button size="lg" variant="secondary" onClick={reset}>
            Start over
          </Button>
          <Button size="lg" disabled={check.isPending || !draft} onClick={() => check.mutate()}>
            {check.isPending ? "Checking…" : "Dry run"}
          </Button>
        </>
      );
    if (step === 3)
      return (
        <>
          <Button size="lg" variant="secondary" onClick={() => setStep(2)}>
            Back to mapping
          </Button>
          <Button
            size="lg"
            disabled={!dry?.ready || start.isPending}
            onClick={() => start.mutate()}
          >
            <PlayIcon data-icon="inline-start" />
            {start.isPending ? "Starting…" : "Start import"}
          </Button>
        </>
      );
    if (step === 4)
      return (
        <Button
          size="lg"
          variant="secondary"
          disabled={!current || !live(current) || current.cancel_requested || cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          {current?.cancel_requested ? "Stopping…" : "Cancel import"}
        </Button>
      );
    return (
      <>
        {current && current.status !== "uploaded" ? (
          <Button
            size="lg"
            variant="secondary"
            disabled={start.isPending}
            onClick={() => start.mutate()}
          >
            Run again
          </Button>
        ) : null}
        <Button size="lg" onClick={reset}>
          New import
        </Button>
      </>
    );
  })();

  return (
    <SetupPage title="Import" slot="setup-import">
      <Wizard steps={STEPS} current={step} footer={footer} aria-label="Import steps">
        {error ? (
          <Notice
            tone="danger"
            title="Something went wrong"
            className="mb-4"
            data-slot="import-error"
          >
            {error}
          </Notice>
        ) : null}

        {step === 0 ? (
          <SettingsSection
            icon={ArrowDownToLineIcon}
            title="Where from"
            description="Re-running an import of the same export writes nothing twice."
          >
            <fieldset className="grid gap-3 sm:grid-cols-2" aria-label="Source">
              {(Object.keys(SOURCES) as Source[]).map((s) => {
                const off = s === "zulip" && !chat.enabled;
                const Icon = SOURCES[s].icon;
                return (
                  <label
                    key={s}
                    data-slot="import-source"
                    className="flex cursor-pointer gap-3 rounded-[10px] border border-border bg-card p-4 has-checked:border-primary has-disabled:cursor-not-allowed has-disabled:opacity-55"
                  >
                    <input
                      type="radio"
                      name="import-source"
                      value={s}
                      checked={source === s}
                      disabled={off}
                      onChange={() => setSource(s)}
                      className="mt-1"
                    />
                    <span className="flex min-w-0 flex-col gap-1">
                      <span className="flex items-center gap-2 font-ui text-sm text-foreground">
                        <Icon className="size-4 text-primary" aria-hidden={true} />
                        {SOURCES[s].label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {off ? "Needs the Chat add-on." : SOURCES[s].hint}
                      </span>
                    </span>
                  </label>
                );
              })}
            </fieldset>
          </SettingsSection>
        ) : null}

        {step === 1 ? (
          <SettingsSection
            icon={UploadIcon}
            title={`Upload the ${SOURCES[source].label} export`}
            description="The file is stored and read; nothing is imported until you start it."
          >
            <UploadZone
              aria-label="Export file"
              accept={SOURCES[source].accept}
              disabled={upload.isPending}
              title={upload.isPending ? "Reading the export…" : "Drop the export here or browse"}
              hint={SOURCES[source].zone}
              icon={FileArchiveIcon}
              onFiles={(files) => {
                const f = files[0];
                if (f) upload.mutate(f);
              }}
            />
          </SettingsSection>
        ) : null}

        {step === 2 && current && draft ? (
          <MappingStep api={api} co={co} run={current} mapping={draft} onMapping={setDraft} />
        ) : null}

        {step === 3 && dry ? <DryRunStep dry={dry} /> : null}

        {step === 4 && current ? <ProgressStep run={current} /> : null}

        {step === 5 && current ? <ReportStep run={current} /> : null}

        {step >= 2 && runId && run.isPending ? (
          <Loading label="Loading the import" rows={3} />
        ) : null}
        {step >= 2 && run.isError ? <LoadError what="The import" error={run.error} /> : null}
      </Wizard>

      <section aria-labelledby="import-runs" className="mt-10 flex min-w-0 flex-col">
        <SectionHeading headingId="import-runs" title="Earlier imports" />
        {runs.isPending ? (
          <Loading label="Loading imports" rows={2} />
        ) : runs.isError ? (
          <LoadError what="Imports" error={runs.error} />
        ) : !runs.data.data.length ? (
          <EmptyState
            variant="card"
            icon={ArrowDownToLineIcon}
            title="No imports yet"
            description="Uploads, dry runs and finished imports are listed here."
          />
        ) : (
          <RunList
            rows={runs.data.data}
            onOpen={async (s) =>
              open(await api.client.importRuns.get({ params: { co, id: s.id } }))
            }
          />
        )}
      </section>
    </SetupPage>
  );
}

function RunList({ rows, onOpen }: { rows: Summary[]; onOpen: (r: Summary) => void }) {
  return (
    <Table data-slot="import-runs">
      <TableCaption className="sr-only">The company's imports, newest first.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">File</TableHead>
          <TableHead scope="col">Status</TableHead>
          <TableHead scope="col" className="max-md:hidden">
            Uploaded
          </TableHead>
          <TableHead scope="col">
            <span className="sr-only">Open</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="max-w-[40vw] truncate">
              <span className="text-muted-foreground">{SOURCES[r.source].label} · </span>
              {r.file_name}
            </TableCell>
            <TableCell>
              <Badge variant={STATUS_BADGE[r.status]}>{r.status}</Badge>
            </TableCell>
            <TableCell className="max-md:hidden">{formatDateTime(r.created_at)}</TableCell>
            <TableCell className="text-right">
              <Button size="sm" variant="ghost" onClick={() => onOpen(r)}>
                Open
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const KIND_LABEL: Record<Container["kind"], string> = {
  space: "Space",
  channel: "Channel",
  board: "Board",
};

function MappingStep({
  api,
  co,
  run,
  mapping,
  onMapping,
}: {
  api: Api;
  co: string;
  run: Run;
  mapping: Mapping;
  onMapping: (m: Mapping) => void;
}) {
  const zulip = run.source === "zulip";
  const spaces = useQuery({
    queryKey: ["company", co, "import-targets", "spaces"],
    queryFn: () => api.client.spaces.list({ params: { co }, query: { limit: 200 } }),
  });
  const channels = useQuery({
    queryKey: ["company", co, "import-targets", "channels"],
    enabled: zulip,
    queryFn: () => api.client.channels.list({ params: { co }, query: { limit: 200 } }),
  });
  const boards = useQuery({
    queryKey: ["company", co, "import-targets", "boards"],
    enabled: !zulip,
    queryFn: () => api.client.boards.list({ params: { co }, query: { limit: 200 } }),
  });
  const people = useQuery({
    queryKey: ["company", co, "import-targets", "people"],
    queryFn: () => api.client.people.list({ params: { co }, query: { limit: 200 } }),
  });
  const existing = (kind: Container["kind"]) =>
    (kind === "space"
      ? (spaces.data?.data ?? []).filter((s) => s.kind !== "personal")
      : kind === "channel"
        ? (channels.data?.data ?? []).filter((c) => c.kind === "space" || c.kind === "team")
        : (boards.data?.data ?? [])
    ).map((x) => ({ id: x.id, name: x.name }));
  const members = (people.data?.data ?? []).filter(
    (p) => p.kind === "person" && p.status === "active",
  );

  const setContainer = (key: string, t: Mapping["containers"][string]) =>
    onMapping({ ...mapping, containers: { ...mapping.containers, [key]: t } });
  const setPerson = (key: string, t: Mapping["people"][string]) =>
    onMapping({ ...mapping, people: { ...mapping.people, [key]: t } });

  return (
    <div className="flex min-w-0 flex-col gap-6" data-slot="import-mapping">
      <SettingsSection
        icon={zulip ? MessagesSquareIcon : SquareKanbanIcon}
        title={zulip ? "Streams → channels" : "Spaces and lists → spaces and boards"}
        description={summaryLine(run)}
      >
        {run.preview.warnings.length ? (
          <Notice tone="info" title="What is left out" className="mb-3">
            <ul className="list-disc pl-4">
              {run.preview.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Notice>
        ) : null}
        <Table data-slot="import-containers">
          <TableCaption className="sr-only">Where each part of the export goes.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">In {zulip ? "Zulip" : "ClickUp"}</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                {zulip ? "Messages" : "Tasks"}
              </TableHead>
              <TableHead scope="col">Here</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {run.preview.containers.map((c) => (
              <ContainerRow
                key={c.key}
                c={c}
                target={mapping.containers[c.key] ?? { action: "skip" }}
                existing={existing(c.kind)}
                onTarget={(t) => setContainer(c.key, t)}
              />
            ))}
          </TableBody>
        </Table>
      </SettingsSection>

      <SettingsSection
        icon={UsersIcon}
        title="People"
        description={
          zulip
            ? "Their messages are theirs when matched; otherwise they import as system messages with the sender's name."
            : "Their tasks are assigned to the member's seat when matched; otherwise they import unassigned."
        }
      >
        {run.preview.people.length === 0 ? (
          <p className="text-sm text-muted-foreground">No people in this export.</p>
        ) : (
          <Table data-slot="import-people">
            <TableCaption className="sr-only">Who each person of the export is here.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="max-md:hidden">
                  In {zulip ? "Zulip" : "ClickUp"}
                </TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  {zulip ? "Messages" : "Tasks"}
                </TableHead>
                <TableHead scope="col">
                  <span className="md:hidden">In {zulip ? "Zulip" : "ClickUp"} → </span>Here
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {run.preview.people.map((p) => (
                <PersonRow
                  key={p.key}
                  p={p}
                  target={mapping.people[p.key] ?? { action: "skip" }}
                  members={members.map((m) => ({ id: m.id, name: m.display_name }))}
                  onTarget={(t) => setPerson(p.key, t)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </SettingsSection>
    </div>
  );
}

function summaryLine(run: Run): string {
  const c = run.preview.counts;
  if (run.source === "zulip")
    return `${c.in_window ?? 0} messages in ${c.channels ?? 0} streams and ${c.topics ?? 0} topics from the last 12 months.`;
  return `${(c.tasks ?? 0) + (c.subtasks ?? 0)} tasks in ${c.boards ?? 0} lists of ${c.spaces ?? 0} spaces.`;
}

function ContainerRow({
  c,
  target,
  existing,
  onTarget,
}: {
  c: Container;
  target: Mapping["containers"][string];
  existing: { id: string; name: string }[];
  onTarget: (t: Mapping["containers"][string]) => void;
}) {
  const id = `import-target-${c.key}`;
  const value = target.action === "existing" ? `existing:${target.id}` : target.action;
  const known = target.action === "existing" && !existing.some((e) => e.id === target.id);
  return (
    <TableRow data-slot="import-container" data-key={c.key}>
      <TableCell className={c.parent_key ? "pl-6" : undefined}>
        <span className="flex min-w-0 flex-col">
          <span className="truncate">
            {c.kind === "channel" ? "#" : ""}
            {c.name}
          </span>
          <span className="text-xs text-muted-foreground">
            {KIND_LABEL[c.kind]}
            {c.is_private ? " · private" : ""}
            {c.topics ? ` · ${c.topics} topics` : ""}
          </span>
        </span>
      </TableCell>
      <TableCell className="tabular-nums max-md:hidden">{c.items}</TableCell>
      <TableCell>
        <div className="flex min-w-0 flex-col gap-1.5">
          <NativeSelect
            id={id}
            aria-label={`Target for ${c.name}`}
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "create") onTarget({ action: "create", name: c.name });
              else if (v === "skip") onTarget({ action: "skip" });
              else onTarget({ action: "existing", id: v.slice("existing:".length) });
            }}
          >
            <NativeSelectOption value="create">
              New {KIND_LABEL[c.kind].toLowerCase()}
            </NativeSelectOption>
            {known && target.action === "existing" ? (
              <NativeSelectOption value={`existing:${target.id}`}>
                The one chosen
              </NativeSelectOption>
            ) : null}
            {existing.map((e) => (
              <NativeSelectOption key={e.id} value={`existing:${e.id}`}>
                {c.kind === "channel" ? "#" : ""}
                {e.name}
              </NativeSelectOption>
            ))}
            <NativeSelectOption value="skip">Skip</NativeSelectOption>
          </NativeSelect>
          {target.action === "create" ? (
            <Input
              aria-label={`Name of the new ${KIND_LABEL[c.kind].toLowerCase()} for ${c.name}`}
              value={target.name}
              maxLength={200}
              onChange={(e) => onTarget({ action: "create", name: e.target.value })}
            />
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

function PersonRow({
  p,
  target,
  members,
  onTarget,
}: {
  p: Person;
  target: Mapping["people"][string];
  members: { id: string; name: string }[];
  onTarget: (t: Mapping["people"][string]) => void;
}) {
  const value = target.action === "match" ? `match:${target.membership_id}` : target.action;
  const who = (
    <span className="flex min-w-0 flex-col">
      <span className="truncate">{p.name}</span>
      {p.email ? <span className="truncate text-xs text-muted-foreground">{p.email}</span> : null}
    </span>
  );
  // Wave 12: below md the person sits above a full-width dropdown, so its longest choice ("Skip
  // (keep the name only)", "Invite …") is never cut off at 390 px.
  return (
    <TableRow data-slot="import-person" data-key={p.key}>
      <TableCell className="max-md:hidden">{who}</TableCell>
      <TableCell className="tabular-nums max-md:hidden">{p.items}</TableCell>
      <TableCell className="max-md:max-w-0 max-md:space-y-1.5">
        <div className="md:hidden" aria-hidden="true">
          {who}
        </div>
        <NativeSelect
          className="max-md:w-full"
          aria-label={`Who ${p.name} is here`}
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "skip") onTarget({ action: "skip" });
            else if (v === "invite") onTarget({ action: "invite" });
            else onTarget({ action: "match", membership_id: v.slice("match:".length) });
          }}
        >
          {members.map((m) => (
            <NativeSelectOption key={m.id} value={`match:${m.id}`}>
              {m.name}
            </NativeSelectOption>
          ))}
          {target.action === "match" && !members.some((m) => m.id === target.membership_id) ? (
            <NativeSelectOption value={value}>
              {p.match?.display_name ?? "The member chosen"}
            </NativeSelectOption>
          ) : null}
          {p.email ? (
            <NativeSelectOption value="invite">Invite {p.email}</NativeSelectOption>
          ) : null}
          <NativeSelectOption value="skip">Skip (keep the name only)</NativeSelectOption>
        </NativeSelect>
      </TableCell>
    </TableRow>
  );
}

const WHAT: Record<string, string> = {
  spaces: "Spaces",
  channels: "Channels",
  boards: "Boards",
  topics: "Topics",
  messages: "Messages",
  tasks: "Tasks",
  invitations: "Invitations",
  identities: "People remembered",
  people: "People",
};

function TallyTable({
  columns,
  slot,
}: {
  columns: { label: string; tally: Tally }[];
  slot: string;
}) {
  const keys = [...new Set(columns.flatMap((c) => Object.keys(c.tally)))].sort(
    (a, b) => Object.keys(WHAT).indexOf(a) - Object.keys(WHAT).indexOf(b),
  );
  if (keys.length === 0) return <p className="text-sm text-muted-foreground">Nothing to write.</p>;
  return (
    <Table data-slot={slot}>
      <TableCaption className="sr-only">Counts by what is written.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">What</TableHead>
          {columns.map((c) => (
            <TableHead key={c.label} scope="col" className="text-right">
              {c.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {keys.map((k) => (
          <TableRow key={k}>
            <TableCell>{WHAT[k] ?? k}</TableCell>
            {columns.map((c) => (
              <TableCell key={c.label} className="text-right tabular-nums">
                {c.tally[k] ?? 0}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Issues({
  title,
  tone,
  items,
}: {
  title: string;
  tone: "danger" | "warning";
  items: { key: string | null; code: string; message: string }[];
}) {
  if (!items.length) return null;
  return (
    <Notice
      tone={tone}
      title={title}
      data-slot={`import-${tone === "danger" ? "conflicts" : "warnings"}`}
    >
      <ul className="list-disc pl-4">
        {items.map((i, n) => (
          <li key={`${i.code}-${i.key ?? n}`}>{i.message}</li>
        ))}
      </ul>
    </Notice>
  );
}

function DryRunStep({ dry }: { dry: DryRun }) {
  return (
    <SettingsSection
      icon={ListChecksIcon}
      title="Dry run"
      description={
        dry.ready
          ? "Nothing has been written yet. This is what the import will do."
          : "Fix the conflicts in the mapping before starting."
      }
    >
      <div className="flex min-w-0 flex-col gap-4" data-slot="import-dry-run">
        <TallyTable
          slot="import-dry-run-counts"
          columns={[
            { label: "New", tally: dry.created },
            { label: "Already here", tally: dry.reused },
            { label: "Skipped", tally: dry.skipped },
          ]}
        />
        <Issues title="Conflicts" tone="danger" items={dry.conflicts} />
        <Issues title="Warnings" tone="warning" items={dry.warnings} />
      </div>
    </SettingsSection>
  );
}

function ProgressStep({ run }: { run: Run }) {
  const { phase, done, total } = run.progress;
  const pct = total > 0 ? Math.round((done / total) * 100) : run.status === "queued" ? 0 : 5;
  return (
    <SettingsSection
      icon={PlayIcon}
      title={run.status === "queued" ? "Waiting for the import job" : "Importing"}
      description="It runs in the background: you can leave this page and come back."
    >
      <div className="flex flex-col gap-2" data-slot="import-progress">
        <Progress value={pct} aria-label="Import progress" />
        <p className="text-sm text-muted-foreground tabular-nums">
          {run.status === "queued" ? "Queued." : `${phase ?? "starting"}: ${done} of ${total}`}
          {run.cancel_requested ? " · stopping at the next batch" : ""}
        </p>
      </div>
    </SettingsSection>
  );
}

function download(run: Run) {
  const blob = new Blob(
    [
      JSON.stringify(
        {
          import_id: run.id,
          source: run.source,
          file_name: run.file_name,
          status: run.status,
          error: run.error,
          report: run.report,
        },
        null,
        2,
      ),
    ],
    { type: "application/json" },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `import-${run.source}-${run.id.slice(-8)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function ReportStep({ run }: { run: Run }) {
  const r = run.report;
  const tone =
    run.status === "succeeded" ? "success" : run.status === "failed" ? "danger" : "warning";
  return (
    <SettingsSection
      icon={ClipboardCheckIcon}
      title="Report"
      description={run.finished_at ? `Finished ${formatDateTime(run.finished_at)}.` : undefined}
      actions={
        r ? (
          <Button size="sm" variant="secondary" onClick={() => download(run)}>
            <ArrowDownToLineIcon data-icon="inline-start" />
            Download report
          </Button>
        ) : undefined
      }
    >
      <div className="flex min-w-0 flex-col gap-4" data-slot="import-report">
        <Notice
          tone={tone}
          title={
            run.status === "succeeded"
              ? "Imported"
              : run.status === "failed"
                ? "The import stopped"
                : "Cancelled"
          }
        >
          {run.status === "succeeded"
            ? "Everything the mapping named is here. Running it again writes nothing twice."
            : run.status === "failed"
              ? `${run.error ?? "An error stopped it."} What was written stays; run it again to finish.`
              : "It stopped before its next batch. What was written stays; run it again to finish."}
        </Notice>
        {r ? (
          <>
            <TallyTable
              slot="import-report-counts"
              columns={[
                { label: "Created", tally: r.created },
                { label: "Already here", tally: r.updated },
                { label: "Skipped", tally: r.skipped },
              ]}
            />
            <Issues title="Errors" tone="danger" items={r.errors} />
            <Issues title="Warnings" tone="warning" items={r.warnings} />
          </>
        ) : null}
      </div>
    </SettingsSection>
  );
}
